// client/src/components/social/ActivityItem.js
import React from 'react';
import {
  Box, Avatar, Typography, Chip, IconButton, Tooltip
} from '@mui/material';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ActivityReactions from '../activity/ActivityReactions';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import StarIcon from '@mui/icons-material/Star';
import AddIcon from '@mui/icons-material/Add';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Link } from 'react-router-dom';
import axiosInstance from '../../axiosInstance';

const COLLECTION_TO_ITEM_TYPE = {
  games: 'game', consoles: 'console', lego: 'lego',
  movies: 'movie', music: 'music', books: 'book', 'board-games': 'board-game'
};

const TYPE_LABELS = {
  added: 'added to collection',
  rated: 'rated',
  wishlisted: 'wishlisted',
  reviewed: 'reviewed',
  photo_posted: 'shared photos of',
  badge_earned: 'earned a badge',
  followed_user: 'followed someone',
  milestone: 'reached a milestone',
  shared: 'shared in chat'
};

const TYPE_ICONS = {
  added: <AddIcon fontSize="small" />,
  rated: <StarIcon fontSize="small" sx={{ color: 'gold' }} />,
  wishlisted: <AddIcon fontSize="small" />,
  reviewed: <RateReviewIcon fontSize="small" />,
  photo_posted: <PhotoCameraIcon fontSize="small" />,
  badge_earned: <EmojiEventsIcon fontSize="small" sx={{ color: 'orange' }} />,
  followed_user: <PersonAddIcon fontSize="small" />,
  milestone: <EmojiEventsIcon fontSize="small" />,
  shared: <PhotoCameraIcon fontSize="small" sx={{ color: '#7C8CFF' }} />
};

/**
 * Single activity feed item.
 *
 * Props:
 *   activity  — Activity document (populated with userId as User object)
 *   currentUserId — logged-in user id
 *   isMutual  — whether current user and activity.userId have a mutual follow
 */
export default function ActivityItem({ activity, currentUserId, isMutual }) {
  const { featureFlags } = useFeatureFlags();
  const [liked, setLiked] = React.useState(
    activity.likes?.some(l => String(l.userId) === String(currentUserId)) || false
  );
  const [likeCount, setLikeCount] = React.useState(activity.likeCount || 0);
  const [reactions, setReactions] = React.useState(activity.reactions || []);

  const handleLike = async () => {
    try {
      if (liked) {
        await axiosInstance.delete(`/activity/${activity._id}/like`);
        setLiked(false);
        setLikeCount(c => c - 1);
      } else {
        await axiosInstance.post(`/activity/${activity._id}/like`);
        setLiked(true);
        setLikeCount(c => c + 1);
      }
    } catch (err) {
      console.error('Like error:', err);
    }
  };

  const user = activity.userId;
  const itemName = activity.metadata?.name || activity.metadata?.title || '';
  const label = TYPE_LABELS[activity.type] || activity.type;
  const icon = TYPE_ICONS[activity.type];
  const timeAgo = new Date(activity.createdAt).toLocaleDateString();
  const itemType = COLLECTION_TO_ITEM_TYPE[activity.collectionType];
  const itemUrl = itemType && activity.itemId ? `/item/${itemType}/${activity.itemId}` : null;
  const isOwnActivity = String(activity.userId?._id || activity.userId) === String(currentUserId);

  return (
    <Box sx={{ display: 'flex', gap: 1.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Avatar
        src={
          activity.isRemote && user?.profileImage
            ? `/api/proxy-fed-image?url=${encodeURIComponent(user.profileImage)}`
            : (user?.profileImage || undefined)
        }
        alt={user?.username}
        sx={{ width: 36, height: 36, flexShrink: 0 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={600} component="span">
            {activity.remoteItem?.actorDisplay || user?.username}
          </Typography>
          {activity.isRemote && activity.remoteItem?.actorDomain && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }} component="span">
              @{activity.remoteItem.actorDomain}
            </Typography>
          )}
          {icon}
          <Typography variant="body2" color="text.secondary" component="span">
            {label}
          </Typography>
          {itemName && (
            <Typography variant="body2" fontWeight={500} component="span" noWrap
              {...(itemUrl ? { component: Link, to: itemUrl, sx: { textDecoration: 'none', color: 'primary.main', '&:hover': { textDecoration: 'underline' } } } : {})}
            >
              {itemName}
            </Typography>
          )}
        </Box>

        {/* Mutual-only: show extra metadata like badge name, review snippet */}
        {isMutual && activity.type === 'badge_earned' && activity.metadata?.badgeName && (
          <Chip
            label={activity.metadata.badgeName}
            size="small"
            icon={<EmojiEventsIcon />}
            sx={{ mt: 0.5 }}
          />
        )}
        {isMutual && activity.type === 'reviewed' && activity.metadata?.reviewSnippet && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
            "{activity.metadata.reviewSnippet}"
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">{timeAgo}</Typography>
          {!isOwnActivity && (
            itemUrl && ['reviewed', 'photo_posted'].includes(activity.type) ? (
              <Typography variant="caption" component={Link} to={itemUrl}
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                View {activity.type === 'reviewed' ? 'review' : 'photo'} →
              </Typography>
            ) : featureFlags.enableRicherReactions ? (
              <ActivityReactions
                activityId={activity._id}
                reactions={reactions}
                currentUserId={currentUserId}
                onReactionChange={setReactions}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={liked ? 'Unlike' : 'Like'}>
                  <IconButton size="small" onClick={handleLike} sx={{ p: 0.5 }}>
                    {liked
                      ? <ThumbUpIcon fontSize="small" color="primary" />
                      : <ThumbUpOutlinedIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                {likeCount > 0 && (
                  <Typography variant="caption" color="text.secondary">{likeCount}</Typography>
                )}
              </Box>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
}
